import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { useApp } from '../App';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to get plain text from HTML
const getPlainTextFromHtml = (html) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Image limit by size (source of truth)
const IMAGE_LIMIT_BY_SIZE = {
  "50": 1,
  "75": 2,
  "100": 3
};

// Maximum file size in bytes (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Centralized copy
const COPY = {
  uploadLabel: "Upload Images",
  helperBySize: {
    "50": "Due to the scale of this ornament, only 1 image is allowed.",
    "75": "Due to the scale of this ornament, only 2 images are allowed.",
    "100": "Due to the scale of this ornament, only 3 images are allowed."
  },
  exceedMessage: "Maximum of {limit} image(s) allowed for the selected size.",
  fileSizeMessage: "File '{filename}' is too large. Maximum file size is 5MB.",
  reduceConfirmTitle: "Reduce uploaded images?",
  reduceConfirmBody: "You've selected a smaller size that allows fewer images. Keep only the first {limit} and remove the rest?",
  reduceConfirmPrimary: "Keep {limit}",
  reduceConfirmSecondary: "Review Images"
};

const LithophaneSpherePage = () => {
  const theme = useThemeClasses();
  const { addToCart, addMultipleToCart, user } = useApp();
  
  // States
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [removeBackgrounds, setRemoveBackgrounds] = useState([]); // Track which images need background removal
  const [backgroundTheme, setBackgroundTheme] = useState('None'); // Selected background theme (default: "None" system theme)
  const [availableThemes, setAvailableThemes] = useState([]); // Dynamically loaded themes from admin
  const [themesLoading, setThemesLoading] = useState(true); // Loading state for themes
  const [engravingText, setEngravingText] = useState('');
  const [selectedSize, setSelectedSize] = useState('75mm');
  const [uploadError, setUploadError] = useState(''); // Upload validation error
  const [isDragOver, setIsDragOver] = useState(false); // Track drag over state
  const [manualOverrides, setManualOverrides] = useState(false);
  const [parameters, setParameters] = useState({});
  const [sizeProfiles, setSizeProfiles] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [addons, setAddons] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [hoveredAddon, setHoveredAddon] = useState(null);
  
  // Quantity states for order items
  const [ornamentQuantity, setOrnamentQuantity] = useState(1);
  const [addonQuantities, setAddonQuantities] = useState({}); // { addonId: quantity }
  
  // Job states
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState('IDLE'); // IDLE, UPLOADING, QUEUED, PROCESSING, READY, FAILED
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // PHASE 12: Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStep, setProcessingStep] = useState(''); // Current processing step description;
  
  // Preview generation tracking
  const [orderId, setOrderId] = useState(null);
  const [regenerationCount, setRegenerationCount] = useState(0);
  const [workingImages, setWorkingImages] = useState([]);
  const [previewConfirmed, setPreviewConfirmed] = useState(false);
  
  // Image zoom/lightbox states
  const [showLightbox, setShowLightbox] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = React.useRef(null);
  
  // AI Generated Prompt Text (for display below preview)
  const [generatedPromptText, setGeneratedPromptText] = useState(null);

  // PHASE 3: Regeneration limit modal
  const [showRegenerationLimitModal, setShowRegenerationLimitModal] = useState(false);
  
  // PHASE 4: Prompts transparency
  const [promptsLog, setPromptsLog] = useState([]);
  const [showPromptsLog, setShowPromptsLog] = useState(false);
  
  // PHASE 5: Designer request modal
  const [showDesignerRequestModal, setShowDesignerRequestModal] = useState(false);
  const [designerRequestImages, setDesignerRequestImages] = useState([]);
  const [designerRequestTheme, setDesignerRequestTheme] = useState('christmas');
  const [designerRequestText, setDesignerRequestText] = useState('');
  const [designerRequestSize, setDesignerRequestSize] = useState('75mm');
  
  // PHASE 6: Draft.js editor for design details
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  
  // PHASE 7: Form validation and submission
  const [isSubmittingDesignerRequest, setIsSubmittingDesignerRequest] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  
  // PHASE 9: Post-submission state
  const [designerRequestSubmitted, setDesignerRequestSubmitted] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // PHASE 10: Next button - STL Generation
  const [showNextComingSoon, setShowNextComingSoon] = useState(false);
  const [stlGenerating, setStlGenerating] = useState(false);
  const [stlJobId, setStlJobId] = useState(null);
  const [stlOrderId, setStlOrderId] = useState(null);
  const [stlStatus, setStlStatus] = useState(null);
  const [stlProgress, setStlProgress] = useState(0);
  const [stlStatusMessage, setStlStatusMessage] = useState('');
  const [stlUrl, setStlUrl] = useState(null);
  const [stlFailureDetails, setStlFailureDetails] = useState(null);
  const [stlGenerationStartTime, setStlGenerationStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Pricing
  const [totalPrice, setTotalPrice] = useState(69.00);
  
  // Background theme options
  // Load available background themes from admin panel on mount
  useEffect(() => {
    const loadThemes = async () => {
      try {
        setThemesLoading(true);
        // Fetch all active themes including system themes (like "None")
        const response = await axios.get(`${API}/admin/ai-products/themes?active_only=true`);
        
        // Transform themes from admin format to component format
        // IMPORTANT: Use theme_name as value (backend expects exact theme_name from database)
        const transformedThemes = response.data.map(theme => ({
          value: theme.theme_name, // Keep original theme_name (e.g., "Christmas", "None")
          label: theme.theme_name,
          icon: theme.icon || '🎨',
          category: theme.category || 'Other',
          id: theme.id,
          isSystemTheme: theme.is_system_theme || false
        }));
        
        // Sort themes: System themes first (like "None"), then by sort_order
        const sortedThemes = transformedThemes.sort((a, b) => {
          if (a.isSystemTheme && !b.isSystemTheme) return -1;
          if (!a.isSystemTheme && b.isSystemTheme) return 1;
          return 0;
        });
        
        setAvailableThemes(sortedThemes);
        console.log(`Loaded ${sortedThemes.length} active themes from admin panel (including system themes)`);
      } catch (error) {
        console.error('Failed to load themes from admin panel:', error);
        // Set empty array on error - user will see "No themes available" message
        setAvailableThemes([]);
      } finally {
        setThemesLoading(false);
      }
    };
    
    loadThemes();
  }, []); // Run once on mount
  
  // Timer for STL generation elapsed time
  useEffect(() => {
    let interval = null;
    
    if (stlGenerating && stlGenerationStartTime && stlStatus !== 'ready' && stlStatus !== 'complete' && stlStatus !== 'failed') {
      // Update elapsed time every second
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - stlGenerationStartTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    } else {
      // Clear timer when generation stops
      if (interval) {
        clearInterval(interval);
      }
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [stlGenerating, stlGenerationStartTime, stlStatus]);
  
  // Check URL parameters for designer request
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const openDesignerRequest = urlParams.get('designerRequest');
    
    if (openDesignerRequest === 'true') {
      setShowDesignerRequestModal(true);
      // Clean up URL without reloading page
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // Run once on mount
  
  // Load configuration from backend on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/ai/lithophane-sphere/config`);
        if (response.data.success) {
          const config = response.data.config;
          setSizeProfiles(config.size_profiles || {});
          const activeProfile = config.active_size_profile || '75mm';
          setSelectedSize(activeProfile);
          setParameters(config.size_profiles[activeProfile] || {});
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
        // Use fallback hardcoded values if API fails
        const fallbackProfiles = {
          '50mm': {
            lithophane_resolution_mm: 0.12,
            sphere_diameter_mm: 50,
            picture_angular_height_deg: 90,
            picture_angular_width_deg: 360.00,
            max_thickness_mm: 2.4,
            min_thickness_mm: 0.6,
            cylinder_outer_diameter_mm: 15.6,
            cylinder_height_mm: 4,
            cylinder_thickness_mm: 2,
            cylinder_ledge_outer_diameter_mm: 17,
            top_hole_outer_diameter_mm: 0,
            x_shift_mm: 0.5,
            y_shift_mm: 0.00,
            base_price: 59.00
          },
          '75mm': {
            lithophane_resolution_mm: 0.12,
            sphere_diameter_mm: 75,
            picture_angular_height_deg: 90,
            picture_angular_width_deg: 360.00,
            max_thickness_mm: 2.4,
            min_thickness_mm: 0.6,
            cylinder_outer_diameter_mm: 15.6,
            cylinder_height_mm: 4,
            cylinder_thickness_mm: 2,
            cylinder_ledge_outer_diameter_mm: 17,
            top_hole_outer_diameter_mm: 0,
            x_shift_mm: 0.5,
            y_shift_mm: 0.00,
            base_price: 69.00
          },
          '100mm': {
            lithophane_resolution_mm: 0.12,
            sphere_diameter_mm: 100,
            picture_angular_height_deg: 90,
            picture_angular_width_deg: 500.00,
            max_thickness_mm: 2.4,
            min_thickness_mm: 0.6,
            cylinder_outer_diameter_mm: 15.6,
            cylinder_height_mm: 4,
            cylinder_thickness_mm: 2,
            cylinder_ledge_outer_diameter_mm: 17,
            top_hole_outer_diameter_mm: 0,
            x_shift_mm: 0.5,
            y_shift_mm: 0.00,
            base_price: 79.00
          }
        };
        setSizeProfiles(fallbackProfiles);
        setParameters(fallbackProfiles['75mm']);
      } finally {
        setLoadingConfig(false);
      }
    };
    
    const loadAddons = async () => {
      try {
        const response = await axios.get(`${API}/ai/lithophane-sphere/addons`);
        if (response.data.success) {
          setAddons(response.data.addons || []);
          // Initialize selected options for each addon
          const initialOptions = {};
          const initialQuantities = {};
          response.data.addons.forEach(addon => {
            initialOptions[addon.id] = false;
            initialQuantities[addon.id] = 1; // Default quantity for each addon
          });
          setSelectedOptions(initialOptions);
          setAddonQuantities(initialQuantities);
        }
      } catch (error) {
        console.error('Failed to load add-ons:', error);
      }
    };
    
    loadConfig();
    loadAddons();
  }, []);
  
  // Get base price for selected size
  const getBasePrice = () => {
    const price = parameters.base_price || sizeProfiles[selectedSize]?.base_price || 69.00;
    return price;
  };
  
  // Get image limit for selected size
  const getImageLimit = () => {
    const sizeKey = selectedSize.replace('mm', '');
    return IMAGE_LIMIT_BY_SIZE[sizeKey] || 3;
  };
  
  // Update total price when options change or size changes
  useEffect(() => {
    let price = getBasePrice() * ornamentQuantity; // Multiply base price by ornament quantity
    // Add prices from selected add-ons with their quantities
    Object.keys(selectedOptions).forEach(addonId => {
      if (selectedOptions[addonId]) {
        const addon = addons.find(a => a.id === addonId);
        if (addon) {
          const quantity = addonQuantities[addonId] || 1;
          price += (addon.price || 0) * quantity;
        }
      }
    });
    setTotalPrice(price);
  }, [selectedOptions, selectedSize, parameters, addons, ornamentQuantity, addonQuantities]);
  
  // Handle size change
  const handleSizeChange = (newSize) => {
    const newSizeKey = newSize.replace('mm', '');
    const newLimit = IMAGE_LIMIT_BY_SIZE[newSizeKey] || 3;
    
    // Check if we have more images than the new limit allows
    if (imageFiles.length > newLimit) {
      const confirmMessage = COPY.reduceConfirmBody.replace('{limit}', newLimit);
      const keepButton = COPY.reduceConfirmPrimary.replace('{limit}', newLimit);
      
      if (window.confirm(`${COPY.reduceConfirmTitle}\n\n${confirmMessage}\n\nClick OK to ${keepButton}, or Cancel to ${COPY.reduceConfirmSecondary}`)) {
        // Keep only the first N images
        setImageFiles(prev => prev.slice(0, newLimit));
        setUploadedImages(prev => prev.slice(0, newLimit));
        setRemoveBackgrounds(prev => prev.slice(0, newLimit));
        toast.success(`Kept the first ${newLimit} image(s)`);
      } else {
        // User wants to review - don't change size
        return;
      }
    }
    
    setSelectedSize(newSize);
    setParameters(sizeProfiles[newSize] || {});
    setManualOverrides(false);
    setUploadError(''); // Clear any upload errors
    setIsDragOver(false); // Clear drag state when size changes
    
    // Clear engraving text when switching to 50mm (since engraving is not available)
    if (newSize === '50mm') {
      setEngravingText('');
    }
  };
  
  // Poll job status with retry logic
  useEffect(() => {
    if (jobId && (jobStatus === 'QUEUED' || jobStatus === 'PROCESSING')) {
      let pollAttempts = 0;
      let consecutiveErrors = 0;
      const maxConsecutiveErrors = 5;
      
      const interval = setInterval(async () => {
        try {
          pollAttempts++;
          const response = await axios.get(`${API}/ai/lithophane-sphere/status/${jobId}`, {
            timeout: 10000, // 10 second timeout
          });
          
          // Reset error counter on successful request
          consecutiveErrors = 0;
          
          const status = response.data.status.toUpperCase();
          setJobStatus(status);
          
          console.log(`📊 [POLLING] Attempt ${pollAttempts}: Status = ${status}`);
          
          // PHASE 12: Update processing step based on status
          if (status === 'PROCESSING') {
            setProcessingStep('Generating preview (this may take 1-2 minutes)...');
          } else if (status === 'QUEUED') {
            setProcessingStep('Waiting in queue...');
          }
          
          if (status === 'READY') {
            console.log('✅ [POLLING] Job complete!');
            setPreviewUrl(response.data.preview_url);
            setProcessingStep('Preview complete!');
            
            // Extract regeneration info from config_snapshot
            if (response.data.config_snapshot) {
              const config = response.data.config_snapshot;
              const newRegenerationCount = config.regeneration_count || 0;
              setRegenerationCount(newRegenerationCount);
              setOrderId(config.order_id || jobId);
              setWorkingImages(config.working_images || []);
              
              // PHASE 3: Show modal when regeneration limit reached
              if (newRegenerationCount >= 2) {
                setTimeout(() => {
                  setShowRegenerationLimitModal(true);
                }, 1000); // Small delay after preview is ready
              }
            }
            
            // Extract generated prompt text for display
            if (response.data.generated_prompt_text) {
              setGeneratedPromptText(response.data.generated_prompt_text);
            }
            
            // PHASE 4: Extract prompts log
            if (response.data.prompts_log) {
              setPromptsLog(response.data.prompts_log);
            }
            
            clearInterval(interval);
            toast.success('Preview ready!');
            
            // Clear progress states
            setTimeout(() => {
              setUploadProgress(0);
              setProcessingStep('');
            }, 2000);
          } else if (status === 'FAILED') {
            const errorMsg = response.data.error || 'Generation failed';
            console.error('❌ [POLLING] Job failed:', errorMsg);
            setError(errorMsg);
            setProcessingStep('');
            clearInterval(interval);
            toast.error(`Preview generation failed: ${errorMsg}`);
          }
        } catch (err) {
          consecutiveErrors++;
          console.warn(`⚠️ [POLLING] Network error (${consecutiveErrors}/${maxConsecutiveErrors}):`, err.message);
          
          // If too many consecutive errors, stop polling and show error
          if (consecutiveErrors >= maxConsecutiveErrors) {
            console.error('❌ [POLLING] Too many consecutive errors, stopping poll');
            clearInterval(interval);
            setProcessingStep('Connection issues - check if job completed');
            toast.warning('Having trouble checking status. Job may still be processing. Please refresh page.');
          }
          // Otherwise, continue polling (job might still be processing)
        }
      }, 5000); // Poll every 5 seconds (increased from 3s to reduce load)
      
      setPollingInterval(interval);
      
      return () => clearInterval(interval);
    }
  }, [jobId, jobStatus]);
  
  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showLightbox) {
        handleCloseLightbox();
      }
    };
    
    if (showLightbox) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [showLightbox]);
  
  // Shared function to process selected files (used by both click and drag-and-drop)
  const processSelectedFiles = (files) => {
    const filesArray = Array.from(files);
    const currentLimit = getImageLimit();
    const availableSlots = currentLimit - imageFiles.length;
    
    // Filter for image files only
    const validImageFiles = filesArray.filter(file => {
      return file.type.startsWith('image/') && 
             ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(file.type);
    });
    
    if (validImageFiles.length === 0) {
      toast.error('Please select valid image files (JPG, PNG, WEBP, HEIC)');
      setUploadError('Please select valid image files (JPG, PNG, WEBP, HEIC)');
      return;
    }
    
    if (availableSlots <= 0) {
      const errorMessage = COPY.exceedMessage.replace('{limit}', currentLimit);
      toast.error(errorMessage);
      setUploadError(errorMessage);
      return;
    }
    
    // Validate file sizes first
    const oversizedFiles = validImageFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      const fileName = oversizedFiles[0].name;
      const errorMessage = COPY.fileSizeMessage.replace('{filename}', fileName);
      toast.error(errorMessage);
      setUploadError(errorMessage);
      return;
    }
    
    // PHASE 12: Validate image dimensions (minimum 1024px)
    const filesToAdd = validImageFiles.slice(0, availableSlots);
    let validatedFiles = [];
    let invalidDimensionsCount = 0;
    
    const validateDimensions = async () => {
      for (const file of filesToAdd) {
        try {
          const img = new Image();
          const objectUrl = URL.createObjectURL(file);
          
          await new Promise((resolve, reject) => {
            img.onload = () => {
              if (img.width >= 1024 || img.height >= 1024) {
                validatedFiles.push(file);
              } else {
                invalidDimensionsCount++;
              }
              URL.revokeObjectURL(objectUrl);
              resolve();
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error('Failed to load image'));
            };
            img.src = objectUrl;
          });
        } catch (err) {
          console.error('Image validation error:', err);
          invalidDimensionsCount++;
        }
      }
      
      if (invalidDimensionsCount > 0) {
        toast.error(`${invalidDimensionsCount} image(s) skipped: minimum dimension is 1024px`);
      }
      
      if (validatedFiles.length === 0) {
        setUploadError('All images are too small. Minimum dimension: 1024px');
        return;
      }
      
      // Add validated files
      setImageFiles(prev => [...prev, ...validatedFiles]);
      
      // Generate preview URLs for display
      const newPreviews = validatedFiles.map(file => URL.createObjectURL(file));
      setUploadedImages(prev => [...prev, ...newPreviews]);
      
      // Initialize background removal flags (all false by default)
      const newFlags = new Array(validatedFiles.length).fill(false);
      setRemoveBackgrounds(prev => [...prev, ...newFlags]);
      
      setUploadError('');
      
      if (validatedFiles.length > 0) {
        toast.success(`${validatedFiles.length} image(s) added successfully`);
      }
    };
    
    validateDimensions();
    
    // Accept only up to the limit
    const filesToAccept = validImageFiles.slice(0, availableSlots);
    const excessFiles = validImageFiles.length - filesToAccept.length;
    
    if (excessFiles > 0) {
      const errorMessage = COPY.exceedMessage.replace('{limit}', currentLimit);
      toast.error(errorMessage);
      setUploadError(errorMessage);
    } else {
      setUploadError(''); // Clear error if within limit and size
    }
  };
  
  // Handle image selection (click upload)
  const handleImageSelect = (e) => {
    processSelectedFiles(e.target.files);
  };
  
  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (imageFiles.length < getImageLimit() && jobStatus === 'IDLE') {
      setIsDragOver(true);
    }
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (imageFiles.length >= getImageLimit() || jobStatus !== 'IDLE') {
      return;
    }
    
    const files = e.dataTransfer.files;
    processSelectedFiles(files);
  };
  
  // Remove image
  const handleRemoveImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setRemoveBackgrounds(prev => prev.filter((_, i) => i !== index));
    setUploadError(''); // Clear error when removing images
  };
  
  // Toggle background removal for specific image
  const handleToggleBackgroundRemoval = (index) => {
    setRemoveBackgrounds(prev => {
      const updated = [...prev];
      updated[index] = !updated[index];
      return updated;
    });
  };
  
  // Generate preview
  const handleGeneratePreview = async () => {
    console.log('🚀 [GENERATE PREVIEW] Button clicked - Starting generation process');
    console.log('📊 [GENERATE PREVIEW] Current state:', {
      imageFiles: imageFiles.length,
      selectedSize,
      backgroundTheme,
      engravingText,
      regenerationCount,
      API
    });
    
    if (imageFiles.length === 0) {
      console.error('❌ [GENERATE PREVIEW] No images uploaded');
      toast.error('Please upload at least 1 image');
      return;
    }
    
    try {
      console.log('✅ [GENERATE PREVIEW] Starting upload/generation process');
      setJobStatus('UPLOADING');
      setError(null);
      setUploadProgress(0);
      setProcessingStep('Preparing images for upload...');
      
      // Upload images first (only if this is the first generation)
      let imageIds = [];
      if (regenerationCount === 0 || workingImages.length === 0) {
        console.log('📤 [UPLOAD] Preparing to upload images');
        const formData = new FormData();
        imageFiles.forEach((file, index) => {
          console.log(`📎 [UPLOAD] Adding file ${index + 1}:`, file.name, file.size, 'bytes');
          formData.append('images', file);
        });
        // Add size parameter for validation
        formData.append('size', selectedSize);
        console.log('📏 [UPLOAD] Size parameter:', selectedSize);
        
        setProcessingStep(`Uploading ${imageFiles.length} image(s)...`);
        
        const uploadUrl = `${API}/ai/lithophane-sphere/upload`;
        console.log('🌐 [UPLOAD] Sending POST request to:', uploadUrl);
        console.log('📋 [UPLOAD] Request details:', {
          url: uploadUrl,
          fileCount: imageFiles.length,
          size: selectedSize
        });
        
        try {
          const uploadResponse = await axios.post(uploadUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`📊 [UPLOAD] Progress: ${percentCompleted}%`);
              setUploadProgress(percentCompleted);
            }
          });
          
          console.log('✅ [UPLOAD] Response received:', uploadResponse.status);
          console.log('📦 [UPLOAD] Response data:', uploadResponse.data);
          
          if (!uploadResponse.data.success) {
            console.error('❌ [UPLOAD] Upload failed - success=false');
            throw new Error('Image upload failed');
          }
          
          imageIds = uploadResponse.data.image_ids;
          console.log('✅ [UPLOAD] Images uploaded successfully. IDs:', imageIds);
          setProcessingStep('Images uploaded successfully!');
        } catch (uploadError) {
          console.error('❌ [UPLOAD] Upload request failed:', uploadError);
          console.error('❌ [UPLOAD] Error details:', {
            message: uploadError.message,
            response: uploadError.response?.data,
            status: uploadError.response?.status
          });
          throw uploadError;
        }
      } else {
        // Use image IDs from working_images for regeneration
        imageIds = workingImages.map(img => img.original_id || img.id);
        console.log('🔄 [REGENERATION] Using existing image IDs:', imageIds);
        setProcessingStep('Using existing images...');
      }
      
      // Generate or use existing order_id
      const currentOrderId = orderId || `order_${Date.now()}`;
      if (!orderId) {
        setOrderId(currentOrderId);
      }
      console.log('📝 [ORDER] Order ID:', currentOrderId);
      
      setProcessingStep('Creating preview job...');
      
      // Create preview job with size and parameters
      const previewPayload = {
        image_ids: imageIds,
        text: engravingText || null,
        size_profile: selectedSize,
        parameters: parameters,
        manual_overrides: manualOverrides,
        remove_backgrounds: removeBackgrounds,
        background_theme: backgroundTheme !== 'none' ? backgroundTheme : null,
        order_id: currentOrderId,
        regeneration_count: regenerationCount,
        working_images: workingImages,
        font_preference: 'default',
        engraving_mode: false
      };
      
      const previewUrl = `${API}/ai/lithophane-sphere/preview`;
      console.log('🎨 [PREVIEW] Creating preview job');
      console.log('🌐 [PREVIEW] POST request to:', previewUrl);
      console.log('📋 [PREVIEW] Payload:', JSON.stringify(previewPayload, null, 2));
      
      try {
        const previewResponse = await axios.post(previewUrl, previewPayload);
        
        console.log('✅ [PREVIEW] Response received:', previewResponse.status);
        console.log('📦 [PREVIEW] Response data:', previewResponse.data);
        
        setJobId(previewResponse.data.job_id);
        setJobStatus('QUEUED');
        setProcessingStep('Job queued for processing...');
        console.log('✅ [PREVIEW] Job created successfully. Job ID:', previewResponse.data.job_id);
        toast.success(`Preview generation started! (${regenerationCount > 0 ? `Regeneration #${regenerationCount}` : 'First generation'})`);
      } catch (previewError) {
        console.error('❌ [PREVIEW] Preview creation failed:', previewError);
        console.error('❌ [PREVIEW] Error details:', {
          message: previewError.message,
          response: previewError.response?.data,
          status: previewError.response?.status
        });
        throw previewError;
      }
      
    } catch (err) {
      console.error('❌ [GENERATE PREVIEW] FATAL ERROR:', err);
      console.error('❌ [GENERATE PREVIEW] Error stack:', err.stack);
      console.error('❌ [GENERATE PREVIEW] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      
      const errorMessage = err.response?.data?.detail || err.message;
      setError(errorMessage);
      setJobStatus('FAILED');
      setProcessingStep('');
      toast.error(`Failed to generate preview: ${errorMessage}`);
    }
  };
  
  // Regenerate preview (max 2 times)
  const handleRegenerate = async () => {
    console.log('🔄 [REGENERATE] Button clicked');
    console.log('📊 [REGENERATE] Current regeneration count:', regenerationCount);
    
    if (regenerationCount >= 2) {
      console.warn('⚠️ [REGENERATE] Maximum regenerations reached');
      toast.error('Maximum regenerations (2) reached');
      return;
    }
    
    // Calculate new regeneration count
    const newRegenerationCount = regenerationCount + 1;
    console.log('🔢 [REGENERATE] New regeneration count:', newRegenerationCount);
    setRegenerationCount(newRegenerationCount);
    
    // Reset preview and prompt text
    setPreviewUrl(null);
    setGeneratedPromptText(null);
    setPromptsLog([]);
    setJobStatus('UPLOADING');
    
    try {
      // Use the same images from current job (use original_id from working_images)
      const imageIds = workingImages.map(img => img.original_id || img.id);
      console.log('🖼️ [REGENERATE] Using image IDs:', imageIds);

      if (imageIds.length === 0) {
        console.error('❌ [REGENERATE] No images available');
        toast.error('No images available for regeneration');
        setJobStatus('IDLE');
        return;
      }

      setJobStatus('QUEUED');

      // Prepare form data for regeneration with incremented count
      const regeneratePayload = {
        image_ids: imageIds,
        text: engravingText || null,
        size_profile: selectedSize,
        parameters: parameters,
        manual_overrides: manualOverrides,
        remove_backgrounds: removeBackgrounds,
        background_theme: backgroundTheme !== 'none' ? backgroundTheme : null,
        order_id: orderId,
        regeneration_count: newRegenerationCount,
        working_images: workingImages,
        font_preference: 'default',
        engraving_mode: false
      };
      
      const previewUrl = `${API}/ai/lithophane-sphere/preview`;
      console.log('🌐 [REGENERATE] POST request to:', previewUrl);
      console.log('📋 [REGENERATE] Payload:', JSON.stringify(regeneratePayload, null, 2));
      
      const previewResponse = await axios.post(previewUrl, regeneratePayload);
      
      console.log('✅ [REGENERATE] Response received:', previewResponse.data);
      
      if (previewResponse.data.success) {
        const newJobId = previewResponse.data.job_id;
        console.log('✅ [REGENERATE] New job created:', newJobId);
        setJobId(newJobId);
        setJobStatus('QUEUED');
        
        toast.success(`Regeneration #${newRegenerationCount} started! (${2 - newRegenerationCount} remaining)`);
      } else {
        console.error('❌ [REGENERATE] Response success=false');
        throw new Error(previewResponse.data.message || 'Regeneration failed');
      }
    } catch (error) {
      console.error('❌ [REGENERATE] Error:', error);
      console.error('❌ [REGENERATE] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(`Regeneration failed: ${error.response?.data?.message || error.message}`);
      setJobStatus('IDLE');
    }
  };
  
  // PHASE 6: Handle editor changes with character limit
  const handleEditorChange = (newEditorState) => {
    const contentState = newEditorState.getCurrentContent();
    const plainText = contentState.getPlainText('');
    
    // Enforce 250 character limit
    if (plainText.length <= 250) {
      setEditorState(newEditorState);
    } else {
      toast.error('Maximum 250 characters allowed');
    }
  };
  
  // Get HTML from editor state
  const getDesignDetailsHtml = () => {
    const contentState = editorState.getCurrentContent();
    return draftToHtml(convertToRaw(contentState));
  };
  
  // Get plain text length from editor
  const getEditorPlainTextLength = () => {
    const contentState = editorState.getCurrentContent();
    return contentState.getPlainText('').length;
  };
  
  // PHASE 7: Validate designer request form
  useEffect(() => {
    const hasImages = designerRequestImages.length > 0;
    const hasSize = designerRequestSize && designerRequestSize.length > 0;
    const hasTheme = designerRequestTheme && designerRequestTheme.length > 0;
    
    setIsFormValid(hasImages && hasSize && hasTheme);
  }, [designerRequestImages, designerRequestSize, designerRequestTheme]);
  
  // PHASE 7: Handle designer request submission - now adds to cart
  const handleSubmitDesignerRequest = async () => {
    if (!isFormValid) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!user) {
      toast.error('Please log in to submit a design request');
      return;
    }
    
    setIsSubmittingDesignerRequest(true);
    
    try {
      // Prepare form data
      const formData = new FormData();
      
      // Add images
      designerRequestImages.forEach((image) => {
        formData.append('images', image);
      });
      
      // Add form fields
      formData.append('size', designerRequestSize);
      formData.append('theme', designerRequestTheme);
      formData.append('optional_text', designerRequestText || '');
      formData.append('design_details', getDesignDetailsHtml());
      formData.append('customer_name', user?.name || user?.email?.split('@')[0] || 'Guest');
      formData.append('customer_email', user?.email || '');
      
      // PHASE 8: Add order_id if user came from regeneration
      if (orderId) {
        formData.append('order_id', orderId);
      }
      
      // Submit to backend (now creates order and returns order_id)
      const response = await axios.post(
        `${API}/designer-requests/submit`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        const designerOrderId = response.data.order_id;
        const designerRequestId = response.data.request_id;
        
        // Get price for the size selected in the Design For Me form
        let designerPrice = 69.00; // Default fallback
        if (sizeProfiles[designerRequestSize]) {
          designerPrice = sizeProfiles[designerRequestSize].base_price || parameters.base_price || 69.00;
        } else {
          // Fallback prices by size
          const fallbackPrices = {
            '50mm': 59.00,
            '75mm': 69.00,
            '100mm': 79.00
          };
          designerPrice = fallbackPrices[designerRequestSize] || 69.00;
        }
        
        console.log(`[Design Request] Size: ${designerRequestSize}, Price: $${designerPrice}`);
        
        // Add design request to cart
        addToCart({
          product_id: `design_request_${designerOrderId}`,
          name: `🎨 Custom Design Request - Lithophane (${designerRequestSize})`,
          description: `Theme: ${designerRequestTheme} | ${designerRequestImages.length} images uploaded`,
          price: designerPrice,
          quantity: 1,
          size: designerRequestSize,
          designer_request_id: designerRequestId,
          designer_order_id: designerOrderId,
          is_custom: true,
          selected_options: {},
          product: {
            product_id: `design_request_${designerOrderId}`,
            name: `🎨 Custom Design Request - Lithophane (${designerRequestSize})`,
            description: `Theme: ${designerRequestTheme}`,
            price: designerPrice
          }
        });
        
        // PHASE 9: Set submission states
        setDesignerRequestSubmitted(true);
        
        // Reset form and close modal
        setDesignerRequestImages([]);
        setDesignerRequestTheme('christmas');
        setDesignerRequestText('');
        setDesignerRequestSize('75mm');
        setEditorState(EditorState.createEmpty());
        setShowDesignerRequestModal(false);
        
        toast.success('Design request added to cart!');
      } else {
        throw new Error(response.data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Designer request submission error:', error);
      toast.error(`Failed to submit request: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsSubmittingDesignerRequest(false);
    }
  };
  
  // Phase 4: Enhanced status polling with diagnostics using job_id
  useEffect(() => {
    let pollInterval = null;
    
    if (stlOrderId && stlGenerating) {
      console.log('[STL Polling] Started for job:', stlOrderId);
      
      // Start polling every 2-3 seconds
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/litho/status/${stlOrderId}`);
          const data = response.data;
          
          // Map status to user-friendly messages
          let displayMessage = data.status_message;
          
          // Override with Phase 4 specified messages
          if (data.status === 'queued') {
            displayMessage = "Waiting to start...";
          } else if (data.status === 'processing') {
            displayMessage = data.status_message || "Generating STL...";
          } else if (data.status === 'uploading') {
            displayMessage = "Uploading STL...";
          } else if (data.status === 'ready') {
            displayMessage = "STL ready!";
          } else if (data.status === 'complete') {
            // Phase 6: Completion message
            displayMessage = data.status_message || "Your lithophane STL is ready and attached to your order!";
          } else if (data.status === 'failed') {
            displayMessage = "Generation failed";
          }
          
          // Display server truth - no client-side regression prevention
          // Server enforces monotonic state transitions
          setStlStatus(data.status);
          setStlProgress(data.progress || 0);
          setStlStatusMessage(displayMessage);
          
          // Phase 3: Handle stalled jobs
          if (data.is_stalled) {
            console.error('[STL Status] Job stalled - timeout detected');
            toast.error('STL generation timed out. Please try again.');
            setStlGenerating(false);
            clearInterval(pollInterval);
            return;
          }
          
          // Check if job is complete
          if (data.status === 'ready' || data.status === 'complete') {
            setStlUrl(data.stl_url);
            setStlGenerating(false);
            
            // Phase 6: Show confirmation message
            const confirmationMessage = "Your lithophane STL is ready and attached to your order!";
            toast.success(`✅ ${confirmationMessage}`);
            clearInterval(pollInterval);
            
          } else if (data.status === 'failed') {
            setStlGenerating(false);
            setStlFailureDetails(data.failure_details || null);
            
            // Phase 5: Enhanced user-friendly error message
            const userMessage = "We couldn't generate your STL. Please try again.";
            toast.error(`❌ ${userMessage}`);
            clearInterval(pollInterval);
            
            // Log full diagnostic info
            console.error('[STL Failed - Phase 5]', {
              order_id: stlOrderId,
              reason: data.failure_details?.reason || data.error,
              error_type: data.failure_details?.error_type,
              artifacts: data.failure_details?.artifacts || [],
              status_message: data.status_message,
              full_data: data
            });
            
            // Log artifacts for support
            if (data.failure_details?.artifacts && data.failure_details.artifacts.length > 0) {
              console.log('[Artifacts Available]', data.failure_details.artifacts);
            }
          }
        } catch (error) {
          console.error('[STL Polling Error]', error);
          // Don't stop polling on network errors, keep trying
        }
      }, 2500); // Poll every 2.5 seconds
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        console.log('[STL Polling] Stopped');
      }
    };
  }, [stlOrderId, stlGenerating]);
  
  // Handle Next button (proceed to STL generation)
  const handleNext = async () => {
    if (stlGenerating) return; // Prevent double submission
    
    try {
      setStlGenerating(true);
      setStlGenerationStartTime(Date.now()); // Start timer
      setElapsedTime(0); // Reset elapsed time
      setShowNextComingSoon(false); // Hide coming soon message if showing
      
      // Show important notification about generation time
      toast(
        'STL Generation Started! This process may take approximately 5–15 minutes, depending on design complexity. Please do not close or refresh your browser until it is complete.',
        {
          duration: 8000, // Show for 8 seconds
          position: 'top-center',
          icon: 'ℹ️',
          style: {
            background: '#0066cc',
            color: '#fff',
            fontWeight: '500',
            fontSize: '16px',
            padding: '16px 24px',
            maxWidth: '600px'
          }
        }
      );
      
      // Validate required data
      if (!previewUrl) {
        toast.error('Please generate a preview first before proceeding');
        setStlGenerating(false);
        return;
      }
      
      if (!jobId) {
        toast.error('No job ID found. Please regenerate the preview');
        setStlGenerating(false);
        return;
      }
      
      // Get the preview image path from the job
      const jobData = await axios.get(`${API}/ai/lithophane-sphere/status/${jobId}`);
      const imagePath = jobData.data.preview_url || previewUrl;
      
      // Generate temporary order ID for tracking
      const tempOrderId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Prepare STL generation payload
      const stlPayload = {
        temp_order_id: tempOrderId,
        source_page: "/ai-products/lithophane-sphere",
        selectedSize: selectedSize,
        image_path: imagePath,
        email: "timelrd@rogers.com", // Fixed email for lithophanemaker.com
        defaults: {
          resolution_mm_per_px: 0.12,
          picture_angular_height_deg: 90,
          picture_angular_width_deg: 360,
          max_thickness_mm: 2.4,
          min_thickness_mm: 0.6,
          cyl_outer_d_mm: 15.6,
          cyl_height_mm: 4,
          cyl_thickness_mm: 2,
          cyl_ledge_od_mm: 17,
          top_hole_od_mm: 0,
          x_shift_0_1: 0.5,
          y_shift_0_1: 0.0,
          checkboxes: {
            "Moon Background": true,
            "Flip Image": true,
            "Crop Image": false,
            "Fit Image to Sphere": false
          }
        },
        timeouts: {
          page_load_ms: 30000,
          render_ms: 90000,
          download_ms: 1200000  // 20 minutes for complex STL generation
        }
      };
      
      // Submit STL generation job (NO order creation yet)
      const response = await axios.post(`${API}/litho/generate`, stlPayload);
      
      if (response.data.job_id) {
        const returnedJobId = response.data.job_id;
        setStlOrderId(returnedJobId); // Store job_id (not order_id yet)
        setStlStatus(response.data.status);
        setStlStatusMessage('Queued for generation...');
        setStlProgress(0);
        
        toast.success('STL generation started! Processing...');
        
        // Polling will start automatically via useEffect
        console.log('STL Job ID:', returnedJobId);
      } else {
        throw new Error('No job_id returned from server');
      }
      
    } catch (error) {
      console.error('Failed to start STL generation:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to start STL generation';
      toast.error(errorMessage);
      setStlGenerating(false);
    }
  };
  
  // Handle STL generation retry
  const handleRetrySTL = async () => {
    // Reset states
    setStlStatus(null);
    setStlProgress(0);
    setStlStatusMessage('');
    setStlUrl(null);
    
    // Retry generation
    await handleNext();
  };
  
  // Handle proceed to checkout after STL is ready
  const handleProceedToCheckout = async () => {
    if (stlStatus !== 'complete' && stlStatus !== 'ready') {
      toast.error('Please wait for STL generation to complete');
      return;
    }
    
    if (!stlUrl) {
      toast.error('STL file not available');
      return;
    }
    
    if (!user) {
      toast.error('Please log in to add to cart');
      return;
    }
    
    try {
      const basePrice = getBasePrice();
      
      // Step 1: Create order in database
      const orderPayload = {
        job_id: stlOrderId, // This is the STL job_id
        preview_job_id: jobId, // This is the preview job_id with image references
        user_email: user.email || 'timelrd@rogers.com',
        selectedSize: selectedSize,
        ornament_quantity: ornamentQuantity,
        addons: Object.keys(selectedOptions)
          .filter(addonId => selectedOptions[addonId])
          .map(addonId => {
            const addon = addons.find(a => a.id === addonId);
            return {
              id: addon.id,
              name: addon.name,
              price: addon.price,
              quantity: addonQuantities[addonId] || 1
            };
          }),
        total_price: totalPrice
      };
      
      const orderResponse = await axios.post(`${API}/litho/create-order`, orderPayload);
      const createdOrderId = orderResponse.data.order_id;
      
      // Update local state with real order_id
      setOrderId(createdOrderId);
      
      console.log('[Order Created]', createdOrderId);
      
      // Step 2: Prepare all cart items
      const cartItems = [];
      
      // 1. Add generated STL file (included, no charge)
      console.log('[Cart] Adding STL file...');
      cartItems.push({
        product_id: `stl_file_${createdOrderId}`,
        name: `🎨 Your Generated Lithophane Design (Order #${createdOrderId.slice(0, 8)})`,
        description: `File: lithophane_sphere_${createdOrderId}.zip`,
        price: 0,
        quantity: 1,
        is_included: true,
        stl_order_id: createdOrderId,
        stl_url: stlUrl,
        image: previewUrl || '/placeholder-sphere.png',
        selected_options: {},
        product: {
          product_id: `stl_file_${createdOrderId}`,
          name: `🎨 Your Generated Lithophane Design (Order #${createdOrderId.slice(0, 8)})`,
          description: `File: lithophane_sphere_${createdOrderId}.zip`,
          price: 0
        }
      });
      
      // 2. Add physical lithophane ornament (base product) with selected quantity
      console.log('[Cart] Adding ornament...', {
        quantity: ornamentQuantity,
        price: basePrice,
        size: selectedSize
      });
      cartItems.push({
        product_id: `custom_lithophane_${createdOrderId}_${selectedSize}`,  // Unique ID with order ID
        name: `🎁 Custom 3D Printed Lithophane Sphere (${selectedSize})`,
        description: `Your personalized lithophane ornament`,
        price: basePrice,
        quantity: ornamentQuantity, // Use selected quantity
        size: selectedSize,
        stl_order_id: createdOrderId,
        image: previewUrl || '/placeholder-sphere.png',
        selected_options: {},
        is_custom: true,  // Mark as custom item
        product: {
          product_id: `custom_lithophane_${createdOrderId}_${selectedSize}`,
          name: `🎁 Custom 3D Printed Lithophane Sphere (${selectedSize})`,
          description: `Your personalized lithophane ornament`,
          price: basePrice
        }
      });
      
      // 3. Add selected add-ons (each as separate cart item) with their quantities
      Object.keys(selectedOptions).forEach(addonId => {
        if (selectedOptions[addonId]) {
          const addon = addons.find(a => a.id === addonId);
          if (addon) {
            const quantity = addonQuantities[addonId] || 1;
            console.log('[Cart] Adding addon...', {
              name: addon.name,
              quantity: quantity,
              price: addon.price
            });
            cartItems.push({
              product_id: `addon_${addon.id}`,
              name: `💡 ${addon.name}`,
              description: addon.description || '',
              price: addon.price,
              quantity: quantity, // Use selected quantity
              addon_id: addon.id,
              related_to: `lithophane_sphere_${selectedSize}`,
              selected_options: {},
              product: {
                product_id: `addon_${addon.id}`,
                name: `💡 ${addon.name}`,
                description: addon.description || '',
                price: addon.price
              }
            });
          }
        }
      });
      
      // Add all items to cart at once (prevents race conditions)
      console.log('[Cart] Adding all items to cart:', cartItems.length);
      addMultipleToCart(cartItems);
      console.log('[Cart] All items added successfully');
      
      // Stay on the same page - don't redirect
      
    } catch (err) {
      console.error('Failed to create order or add to cart:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to add items to cart';
      toast.error(errorMessage);
    }
  };
  
  // Lightbox handlers
  const handleOpenLightbox = () => {
    setShowLightbox(true);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };
  
  const handleCloseLightbox = () => {
    setShowLightbox(false);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setIsPanning(false);
  };
  
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 5)); // Increased max to 5x, smaller increments
  };
  
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };
  
  const handleResetZoom = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };
  const handleFitToScreen = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoomLevel(prev => Math.max(0.5, Math.min(5, prev + delta)));
  };

  // Pan functionality
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && zoomLevel > 1) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Touch support for mobile
  const handleTouchStart = (e) => {
    if (zoomLevel > 1 && e.touches.length === 1) {
      setIsPanning(true);
      setPanStart({ 
        x: e.touches[0].clientX - panOffset.x, 
        y: e.touches[0].clientY - panOffset.y 
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isPanning && zoomLevel > 1 && e.touches.length === 1) {
      setPanOffset({
        x: e.touches[0].clientX - panStart.x,
        y: e.touches[0].clientY - panStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (showLightbox) {
        switch(e.key) {
          case 'Escape':
            handleCloseLightbox();
            break;
          case '+':
          case '=':
            handleZoomIn();
            break;
          case '-':
          case '_':
            handleZoomOut();
            break;
          case '0':
            handleResetZoom();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showLightbox, zoomLevel]);
  
  // Add to cart
  const handleAddToCart = async () => {
    if (!jobId || jobStatus !== 'READY') {
      toast.error('Please wait for preview to be ready');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/ai/lithophane-sphere/add-to-cart`, {
        job_id: jobId,
        options: selectedOptions,
        size: selectedSize
      });
      
      if (response.data.success) {
        // Add to cart via App context
        const cartItem = response.data.cart_item;
        addToCart({
          id: cartItem.id,
          name: `AI Lithophane Ornament (${selectedSize})`,
          price: cartItem.total_price,
          quantity: 1,
          image: previewUrl || '/placeholder-sphere.png'
        });
        
        toast.success('Added to cart!');
        
        // Reset form
        setImageFiles([]);
        setUploadedImages([]);
        setEngravingText('');
        setRemoveBackgrounds([]);
        setBackgroundTheme('none');
        setUploadError(''); // Clear upload error
        // Reset addon selections
        const resetOptions = {};
        addons.forEach(addon => {
          resetOptions[addon.id] = false;
        });
        setSelectedOptions(resetOptions);
        setJobId(null);
        setJobStatus('IDLE');
        setPreviewUrl(null);
        setGeneratedPromptText(null);
        
        // Reset regeneration state
        setOrderId(null);
        setRegenerationCount(0);
        setWorkingImages([]);
        setPreviewConfirmed(false);
      }
    } catch (err) {
      console.error('Failed to add to cart:', err);
      toast.error('Failed to add to cart');
    }
  };
  
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">AI Lithophane Ornament</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Upload up to 3 photos + optional text. Get an instant AI preview before you order.
          </p>
        </div>
        
        {/* How It Works */}
        <div className={`${theme.card} rounded-lg shadow-lg p-6 mb-8`}>
          <h3 className="text-xl font-semibold mb-4">✨ How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl mb-2">📸</div>
              <h4 className="font-semibold mb-1">1. Upload Images</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Select 1-3 photos</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">✍️</div>
              <h4 className="font-semibold mb-1">2. Add Text</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Optional engraving</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🎨</div>
              <h4 className="font-semibold mb-1">3. Generate Preview</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">AI creates design</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🛒</div>
              <h4 className="font-semibold mb-1">4. Add to Cart</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Complete order</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative">
          {/* Left Column - Input (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Upload */}
            <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
              <h3 className="text-xl font-semibold mb-4">
                📸 {COPY.uploadLabel} (1-{getImageLimit()})
              </h3>
              
              {/* Helper text */}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" id="upload-helper">
                {COPY.helperBySize[selectedSize.replace('mm', '')]}
              </p>
              
              {/* Upload Error */}
              {uploadError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg" aria-live="polite">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    ⚠️ {uploadError}
                  </p>
                </div>
              )}
              
              {/* Upload Area */}
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                  imageFiles.length >= getImageLimit() 
                    ? 'border-gray-300 bg-gray-50 dark:bg-gray-800' 
                    : isDragOver
                      ? 'border-purple-600 bg-purple-100 dark:bg-purple-800/30 scale-105'
                      : 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="image-upload"
                  disabled={imageFiles.length >= getImageLimit() || jobStatus !== 'IDLE'}
                  aria-describedby="upload-helper"
                  aria-invalid={uploadError ? 'true' : 'false'}
                />
                <label
                  htmlFor="image-upload"
                  className={`cursor-pointer ${imageFiles.length >= getImageLimit() || jobStatus !== 'IDLE' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="text-5xl mb-2">📤</div>
                  <p className="font-semibold mb-2">
                    {isDragOver ? 'Drop images here' : 'Click or drag to upload images'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    JPG, PNG, WEBP • Max 5MB each • Min 1024px
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    {imageFiles.length}/{getImageLimit()} images uploaded
                  </p>
                  {isDragOver && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 font-medium">
                      Release to upload
                    </p>
                  )}
                </label>
              </div>
              
              {/* Background Removal Info Message */}
              {uploadedImages.length > 0 && (
                <div className="mt-4 mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <span className="text-lg">💡</span>
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Background Removal Option
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                        Check "Remove BG" on any image thumbnail below to automatically remove its background when generating your preview. Perfect for portraits and product photos!
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Image Previews */}
              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  {uploadedImages.map((img, index) => (
                    <div key={index} className="relative">
                      <img
                        src={img}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                        disabled={jobStatus !== 'IDLE'}
                      >
                        ×
                      </button>
                      
                      {/* Background Removal Checkbox */}
                      <div className="absolute bottom-2 left-2 bg-white dark:bg-gray-800 rounded-lg px-2 py-1 shadow-md flex items-center space-x-1">
                        <input
                          type="checkbox"
                          id={`remove-bg-${index}`}
                          checked={removeBackgrounds[index] || false}
                          onChange={() => handleToggleBackgroundRemoval(index)}
                          disabled={jobStatus !== 'IDLE'}
                          className="w-4 h-4 cursor-pointer"
                        />
                        <label
                          htmlFor={`remove-bg-${index}`}
                          className="text-xs font-medium cursor-pointer whitespace-nowrap"
                        >
                          Remove BG
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Background Theme Selector */}
            <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
              <h3 className="text-xl font-semibold mb-4">🎨 AI Background Theme</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Choose a theme and let AI generate a beautiful background for your lithophane ornament
              </p>
              
              <div className="relative">
                <select
                  value={backgroundTheme}
                  onChange={(e) => setBackgroundTheme(e.target.value)}
                  disabled={jobStatus !== 'IDLE' || themesLoading}
                  className={`w-full px-4 py-3 border rounded-lg appearance-none cursor-pointer ${theme.input} pr-10`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  {themesLoading ? (
                    <option disabled>Loading themes...</option>
                  ) : availableThemes.length === 0 ? (
                    <>
                      <option value="none">⚪ None / No Background</option>
                      <option disabled>No other themes available — add themes in Admin Portal → AI Products Manager → Background Themes</option>
                    </>
                  ) : (
                    /* Group themes by category - includes "None" from database */
                    [...new Set(availableThemes.map(t => t.category))].sort((a, b) => {
                      // System category always first
                      if (a === 'System' && b !== 'System') return -1;
                      if (a !== 'System' && b === 'System') return 1;
                      return a.localeCompare(b);
                    }).map(category => (
                      <optgroup key={category} label={category === 'System' ? '━━━ Default ━━━' : `━━━ ${category} ━━━`}>
                        {availableThemes
                          .filter(t => t.category === category)
                          .sort((a, b) => {
                            // System themes first within category
                            if (a.isSystemTheme && !b.isSystemTheme) return -1;
                            if (!a.isSystemTheme && b.isSystemTheme) return 1;
                            return a.label.localeCompare(b.label);
                          })
                          .map(themeItem => (
                            <option key={themeItem.id} value={themeItem.value}>
                              {themeItem.icon} {themeItem.label}
                            </option>
                          ))
                        }
                      </optgroup>
                    ))
                  )}
                </select>
              </div>
              
              {/* Show selected theme info */}
              {backgroundTheme && backgroundTheme !== 'none' && availableThemes.length > 0 && (
                <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <p className="text-sm text-purple-900 dark:text-purple-100">
                    <span className="font-medium">Selected:</span> {availableThemes.find(t => t.value === backgroundTheme)?.icon} {availableThemes.find(t => t.value === backgroundTheme)?.label}
                  </p>
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                    {availableThemes.find(t => t.value === backgroundTheme)?.label === 'None' 
                      ? 'A simple, clean background will be generated'
                      : `AI will generate a unique ${availableThemes.find(t => t.value === backgroundTheme)?.label.toLowerCase()}-themed background for your ornament`
                    }
                  </p>
                </div>
              )}
            </div>
            
            {/* Text Input or Size Warning */}
            {selectedSize === '50mm' ? (
              // Warning message for 50mm size
              <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-amber-700 dark:text-amber-400">
                      Limited Engraving Space
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      Due to the scale of this ornament, space for engraving is limited.
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-2">
                      If you'd like to add a custom engraving or text, please select a larger size (75mm or 100mm).
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // Original text input section for 75mm and 100mm
              <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
                <h3 className="text-xl font-semibold mb-4">✍️ Optional Text/Engraving</h3>
                <input
                  type="text"
                  value={engravingText}
                  onChange={(e) => setEngravingText(e.target.value)}
                  maxLength={20}
                  placeholder="Enter custom text (max 20 characters)"
                  className={`w-full px-4 py-3 border rounded-lg ${theme.input}`}
                  disabled={jobStatus !== 'IDLE'}
                />
                <p className="text-sm text-gray-500 mt-2">
                  {engravingText.length}/20 characters
                </p>
              </div>
            )}
            
            {/* Size Selection */}
            <div className={`${theme.card} rounded-lg shadow-lg p-6`}>
              <h3 className="text-xl font-semibold mb-4">📏 Size & Pricing *</h3>
              
              {loadingConfig ? (
                <div className="text-center py-4 text-gray-500">Loading pricing...</div>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    {/* 50mm Option */}
                    <label className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedSize === '50mm' 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-300'
                    }`}>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="size"
                          value="50mm"
                          checked={selectedSize === '50mm'}
                          onChange={(e) => handleSizeChange(e.target.value)}
                          disabled={jobStatus !== 'IDLE'}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-semibold">50mm - Compact</div>
                          <div className="text-xs text-gray-500">Perfect for small displays</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-600">
                          ${(sizeProfiles['50mm']?.base_price || 59).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">base price</div>
                      </div>
                    </label>

                    {/* 75mm Option */}
                    <label className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedSize === '75mm' 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-300'
                    }`}>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="size"
                          value="75mm"
                          checked={selectedSize === '75mm'}
                          onChange={(e) => handleSizeChange(e.target.value)}
                          disabled={jobStatus !== 'IDLE'}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-semibold">75mm - Standard ⭐</div>
                          <div className="text-xs text-gray-500">Most popular choice</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-600">
                          ${(sizeProfiles['75mm']?.base_price || 69).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">base price</div>
                      </div>
                    </label>

                    {/* 100mm Option */}
                    <label className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedSize === '100mm' 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                        : 'border-gray-300 dark:border-gray-600 hover:border-purple-300'
                    }`}>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="size"
                          value="100mm"
                          checked={selectedSize === '100mm'}
                          onChange={(e) => handleSizeChange(e.target.value)}
                          disabled={jobStatus !== 'IDLE'}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-semibold">100mm - Large</div>
                          <div className="text-xs text-gray-500">Maximum detail & impact</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-purple-600">
                          ${(sizeProfiles['100mm']?.base_price || 79).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">base price</div>
                      </div>
                    </label>
                  </div>
                  
                  {/* Size Preview */}
                  <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="text-center">
                      <div className="text-4xl mb-2">🌐</div>
                      <div className="font-semibold text-lg">Selected: {selectedSize}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Diameter: <strong>{parameters.sphere_diameter_mm}mm</strong> • 
                        Resolution: <strong>{parameters.lithophane_resolution_mm}mm</strong>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Options */}
            <div className={`${theme.card} rounded-lg shadow-lg p-6 relative`}>
              <h3 className="text-lg font-semibold mb-2">Recommended Add-Ons</h3>
              <p className="text-xs text-gray-500 mb-2">Enhance your lithophane sphere experience</p>
              
              {/* Hover Info Box - Below subtitle */}
              {hoveredAddon ? (
                <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border border-purple-200 dark:border-purple-700 shadow-md animate-fadeIn">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                    {hoveredAddon.name}
                  </div>
                </div>
              ) : (
                <div className="mb-3 p-3 h-10"></div>
              )}
              
              {addons.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No add-ons available</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {addons.map(addon => (
                    <label 
                      key={addon.id}
                      className={`group relative cursor-pointer transition-all ${
                        selectedOptions[addon.id] ? 'scale-[1.02]' : ''
                      }`}
                      onMouseEnter={() => setHoveredAddon(addon)}
                      onMouseLeave={() => setHoveredAddon(null)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOptions[addon.id] || false}
                        onChange={(e) => setSelectedOptions({
                          ...selectedOptions,
                          [addon.id]: e.target.checked
                        })}
                        className="sr-only"
                      />
                      <div className={`relative rounded-md border overflow-hidden transition-all ${
                        selectedOptions[addon.id]
                          ? 'border-purple-500 shadow-md'
                          : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                      }`}>
                        {/* Image */}
                        <div className="aspect-square bg-gray-100 dark:bg-gray-800 relative">
                          {addon.images && addon.images.length > 0 ? (
                            <img
                              src={addon.images[0]}
                              alt={addon.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <span className="text-xl">📦</span>
                            </div>
                          )}
                          
                          {/* Checkbox Indicator */}
                          {selectedOptions[addon.id] && (
                            <div className="absolute top-1 right-1 w-4 h-4 bg-purple-600 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="p-2 bg-white dark:bg-gray-900">
                          <div className="font-medium text-xs truncate mb-0.5">{addon.name}</div>
                          <div className="text-purple-600 dark:text-purple-400 font-semibold text-xs">
                            +${addon.price.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {/* Total Price Summary */}
              {addons.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Total Price:</span>
                    <span className="text-xl font-bold text-purple-600 dark:text-purple-400">
                      ${totalPrice.toFixed(2)} CAD
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column - Order Summary & Preview (1/3 width) */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-8">
              {/* Preview - MOVED TO TOP - 50% LARGER */}
              <div className={`${theme.card} rounded-lg shadow-2xl p-8`}>
                <h3 className="text-2xl font-bold mb-6">Preview</h3>
                
                {jobStatus === 'IDLE' && (
                  <div className="text-center py-20 text-gray-400">
                    <div className="text-8xl mb-6">🌐</div>
                    <p className="text-lg">Preview will appear here</p>
                  </div>
                )}
                
                {(jobStatus === 'UPLOADING' || jobStatus === 'QUEUED' || jobStatus === 'PROCESSING') && (
                  <div className="text-center py-20">
                    <div className="text-8xl mb-6 animate-pulse">⏳</div>
                    <p className="text-lg font-bold">Generating...</p>
                    <p className="text-base text-gray-500 mt-3">This takes 1-2 minutes</p>
                    
                    {/* PHASE 12: Processing step indicator */}
                    {processingStep && (
                      <p className="text-sm text-blue-600 dark:text-blue-400 mt-3 animate-pulse">
                        {processingStep}
                      </p>
                    )}
                    
                    {/* PHASE 12: Upload progress bar */}
                    {jobStatus === 'UPLOADING' && uploadProgress > 0 && (
                      <div className="mt-6 max-w-md mx-auto">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-purple-600 to-blue-600 h-3 transition-all duration-300 ease-out"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{uploadProgress}% uploaded</p>
                      </div>
                    )}
                    
                    {regenerationCount > 0 && (
                      <p className="text-sm text-purple-600 dark:text-purple-400 mt-2">
                        Regeneration #{regenerationCount}
                      </p>
                    )}
                  </div>
                )}
                
                {jobStatus === 'READY' && previewUrl && (
                  <div className="space-y-5">
                    <div 
                      className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden cursor-pointer hover:ring-4 hover:ring-purple-500 transition-all"
                      onClick={handleOpenLightbox}
                      title="Click to zoom"
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`}
                        alt="Lithophane Preview"
                        className="w-full"
                      />
                    </div>
                    
                    {/* Preview hint */}
                    <div className="text-center space-y-2" id="preview-hint">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        This is your AI-generated preview. You may regenerate up to 2 times or click Next to proceed.
                      </p>
                      <p className="text-xs text-purple-600 dark:text-purple-400">
                        💡 Click image to zoom
                      </p>
                      {regenerationCount < 2 && (
                        <p className="text-xs text-purple-600 dark:text-purple-400 font-semibold" id="regen-remaining">
                          {2 - regenerationCount} regeneration{2 - regenerationCount !== 1 ? 's' : ''} remaining
                        </p>
                      )}
                      {regenerationCount >= 2 && (
                        <p className="text-xs text-red-600 dark:text-red-400 font-semibold">
                          Regeneration limit reached
                        </p>
                      )}
                    </div>
                    
                    {/* Action buttons - Hidden when STL is ready/complete */}
                    {!(stlStatus === 'ready' || stlStatus === 'complete') && (
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          id="btn-regenerate"
                          onClick={handleRegenerate}
                          disabled={regenerationCount >= 2 || stlGenerating}
                          className="py-3 text-base bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          🔄 Regenerate
                        </button>
                        <button
                          id="btn-next"
                          onClick={handleNext}
                          disabled={stlGenerating}
                          className="py-3 text-base bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {stlGenerating ? '⏳ Generating STL...' : '➡️ Next'}
                        </button>
                      </div>
                    )}
                    
                    {/* PHASE 10: Coming Soon Tooltip for Next Button */}
                    {showNextComingSoon && (
                      <div className="mt-3 animate-fade-in">
                        <div className="relative bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/30 dark:to-orange-900/30 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg p-5 shadow-2xl">
                          {/* Close button */}
                          <button
                            onClick={() => setShowNextComingSoon(false)}
                            className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full bg-yellow-200 dark:bg-yellow-800 hover:bg-yellow-300 dark:hover:bg-yellow-700 transition-colors"
                            aria-label="Close"
                          >
                            <svg className="w-4 h-4 text-yellow-900 dark:text-yellow-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          
                          <div className="flex items-start gap-3">
                            <span className="text-3xl">🚧</span>
                            <div className="pr-6">
                              <h4 className="font-bold text-yellow-900 dark:text-yellow-200 text-lg mb-2">
                                Coming Soon!
                              </h4>
                              <p className="text-sm text-yellow-800 dark:text-yellow-300 leading-relaxed">
                                3D ornament rendering will be available in the next update! We are still refining and testing the image generation that you see now.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Phase 2: STL Generation Real-time Status */}
                    {stlOrderId && (
                      <div className="mt-3 animate-fade-in">
                        <div className={`relative border-2 rounded-lg p-5 shadow-2xl ${
                          stlStatus === 'ready' || stlStatus === 'complete'
                            ? 'bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/30 dark:to-teal-900/30 border-green-400 dark:border-green-600'
                            : stlStatus === 'failed'
                            ? 'bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/30 dark:to-pink-900/30 border-red-400 dark:border-red-600'
                            : 'bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border-blue-400 dark:border-blue-600'
                        }`}>
                          <div className="flex items-start gap-3">
                            <span className="text-3xl">
                              {stlStatus === 'ready' || stlStatus === 'complete' ? '✅' : stlStatus === 'failed' ? '❌' : stlGenerating ? '⏳' : '🔄'}
                            </span>
                            <div className="pr-6 flex-1">
                              <h4 className={`font-bold text-lg mb-2 ${
                                stlStatus === 'ready' || stlStatus === 'complete'
                                  ? 'text-green-900 dark:text-green-200'
                                  : stlStatus === 'failed'
                                  ? 'text-red-900 dark:text-red-200'
                                  : 'text-blue-900 dark:text-blue-200'
                              }`}>
                                {stlStatus === 'ready' || stlStatus === 'complete' ? 'STL Ready!' : stlStatus === 'failed' ? 'Generation Failed' : 'Generating STL...'}
                              </h4>
                              
                              {/* Status message */}
                              <p className={`text-sm leading-relaxed mb-3 ${
                                stlStatus === 'ready' || stlStatus === 'complete'
                                  ? 'text-green-800 dark:text-green-300'
                                  : stlStatus === 'failed'
                                  ? 'text-red-800 dark:text-red-300'
                                  : 'text-blue-800 dark:text-blue-300'
                              }`}>
                                {stlStatusMessage || 'Processing your ornament...'}
                              </p>
                              
                              {/* Important: Generation time warning - visible during processing */}
                              {stlGenerating && stlStatus !== 'ready' && stlStatus !== 'complete' && stlStatus !== 'failed' && (
                                <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
                                  <div className="flex items-start gap-2">
                                    <span className="text-yellow-600 dark:text-yellow-400 text-lg flex-shrink-0">⚠️</span>
                                    <div className="flex-1">
                                      <div className="flex justify-between items-start mb-1">
                                        <p className="text-xs font-bold text-yellow-900 dark:text-yellow-200">
                                          Please Wait - Do Not Close Browser
                                        </p>
                                        <div className="flex items-center gap-2 ml-2">
                                          <span className="text-yellow-700 dark:text-yellow-300 text-xs">⏱️</span>
                                          <span className="font-mono font-bold text-sm text-yellow-800 dark:text-yellow-200">
                                            {Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')}
                                          </span>
                                        </div>
                                      </div>
                                      <p className="text-xs text-yellow-900 dark:text-yellow-200">
                                        This process may take approximately 5–15 minutes, depending on design complexity. Please do not close or refresh your browser until it is complete.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Progress bar */}
                              {stlGenerating && stlStatus !== 'ready' && stlStatus !== 'complete' && stlStatus !== 'failed' && (
                                <div className="mb-3">
                                  <div className="flex justify-between text-xs text-blue-700 dark:text-blue-400 mb-1">
                                    <span>Progress</span>
                                    <span>{stlProgress}%</span>
                                  </div>
                                  <div className="w-full bg-blue-200 dark:bg-blue-900/50 rounded-full h-2.5">
                                    <div 
                                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 rounded-full transition-all duration-500"
                                      style={{ width: `${stlProgress}%` }}
                                    ></div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Order ID */}
                              <p className={`text-xs font-mono px-2 py-1 rounded inline-block ${
                                stlStatus === 'ready' || stlStatus === 'complete'
                                  ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/50'
                                  : stlStatus === 'failed'
                                  ? 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/50'
                                  : 'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50'
                              }`}>
                                Order ID: {stlOrderId}
                              </p>
                              
                              {/* Phase 4: Action buttons based on status */}
                              <div className="mt-4 flex gap-2">
                                {/* Retry button for failed jobs */}
                                {stlStatus === 'failed' && (
                                  <button
                                    onClick={handleRetrySTL}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                                  >
                                    🔄 Retry Generation
                                  </button>
                                )}
                                
                                {/* Download button moved - Add to Cart is now in Your Order section */}
                              </div>
                              
                              {/* Phase 5: Enhanced diagnostic info for failed jobs */}
                              {stlStatus === 'failed' && (
                                <details className="mt-3">
                                  <summary className="text-xs text-red-700 dark:text-red-400 cursor-pointer hover:underline">
                                    View diagnostic info & artifacts
                                  </summary>
                                  <div className="mt-2 p-3 bg-red-100 dark:bg-red-900/30 rounded text-xs text-red-900 dark:text-red-200 space-y-2">
                                    <div>
                                      <strong>Order ID:</strong>
                                      <span className="ml-2 font-mono">{stlOrderId}</span>
                                    </div>
                                    <div>
                                      <strong>Status:</strong>
                                      <span className="ml-2">{stlStatus}</span>
                                    </div>
                                    <div>
                                      <strong>Message:</strong>
                                      <span className="ml-2">{stlStatusMessage}</span>
                                    </div>
                                    
                                    {stlFailureDetails && (
                                      <>
                                        <div className="pt-2 border-t border-red-300 dark:border-red-700">
                                          <strong>Failure Reason:</strong>
                                          <p className="ml-2 mt-1">{stlFailureDetails.reason}</p>
                                        </div>
                                        
                                        {stlFailureDetails.artifacts && stlFailureDetails.artifacts.length > 0 && (
                                          <div className="pt-2 border-t border-red-300 dark:border-red-700">
                                            <strong>Available Artifacts:</strong>
                                            <ul className="ml-4 mt-1 space-y-1 list-disc">
                                              {stlFailureDetails.artifacts.map((artifact, idx) => (
                                                <li key={idx} className="font-mono text-xs break-all">
                                                  {artifact}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </>
                                    )}
                                    
                                    <p className="pt-2 text-xs opacity-75 border-t border-red-300 dark:border-red-700">
                                      💡 Full error details are logged in browser console (F12)
                                    </p>
                                  </div>
                                </details>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Generated AI Prompt Display - Hidden when STL is ready/complete */}
                    {generatedPromptText && !(stlStatus === 'ready' || stlStatus === 'complete') && (
                      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-purple-200 dark:border-purple-700">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-2xl">🤖</span>
                          <h3 className="text-lg font-bold text-purple-700 dark:text-purple-300">AI Generation Prompt</h3>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 mb-2">
                            <span className="text-base">ℹ️</span>
                            <p>This is the exact prompt used to generate your greyscale/black & white preview background.</p>
                          </div>
                          <div className="max-h-48 overflow-y-auto p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                              {generatedPromptText}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-2">
                            <span>🎨</span>
                            <span>Greyscale Mode: All colors converted to black & white for optimal lithophane light transmission</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* PHASE 4: AI Prompts Used (Collapsible) - Hidden when STL is ready/complete */}
                    {promptsLog.length > 0 && !(stlStatus === 'ready' || stlStatus === 'complete') && (
                      <div className="mt-6 p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border-2 border-purple-300 dark:border-purple-600">
                        {/* PHASE 9: Designer Requested Note */}
                        {designerRequestSubmitted && (
                          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 rounded">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">✓</span>
                              <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                                Human designer requested after AI generations
                              </p>
                            </div>
                            <p className="text-xs text-green-700 dark:text-green-400 mt-1 ml-8">
                              Our design team will create a custom version based on your preferences
                            </p>
                          </div>
                        )}
                        
                        {/* Header - Clickable to toggle */}
                        <button
                          onClick={() => setShowPromptsLog(!showPromptsLog)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">📋</span>
                            <h3 className="text-lg font-bold text-purple-700 dark:text-purple-300">
                              AI Prompts Used ({promptsLog.length} steps)
                            </h3>
                          </div>
                          <span className="text-2xl text-purple-600 dark:text-purple-400 transition-transform" style={{ transform: showPromptsLog ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            ▼
                          </span>
                        </button>
                        
                        {/* Collapsible Content */}
                        {showPromptsLog && (
                          <div className="mt-4 space-y-4 select-none" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
                            {/* Info Banner */}
                            <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 mb-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded">
                              <span className="text-base">ℹ️</span>
                              <p>Below are all the prompts used in each step of your preview generation. This includes both AI-powered and algorithmic steps.</p>
                            </div>
                            
                            {/* Prompts List */}
                            {promptsLog.map((prompt, index) => (
                              <div key={index} className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700 shadow-sm">
                                {/* Step Header */}
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                      Step {index + 1}
                                    </span>
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                      {prompt.step}
                                    </span>
                                    {prompt.ai_used && (
                                      <span className="px-2 py-1 text-xs font-bold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                                        AI POWERED
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(prompt.timestamp).toLocaleTimeString()}
                                  </span>
                                </div>
                                
                                {/* Provider Info (if AI used) */}
                                {prompt.ai_used && prompt.provider && (
                                  <div className="mb-2 text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-semibold">Provider:</span> {prompt.provider} 
                                    {prompt.model && <span> | <span className="font-semibold">Model:</span> {prompt.model}</span>}
                                  </div>
                                )}
                                
                                {/* Prompt Text */}
                                <div className="max-h-64 overflow-y-auto p-3 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
                                    {prompt.prompt}
                                  </p>
                                </div>
                              </div>
                            ))}
                            
                            {/* Stats Footer */}
                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mt-4 pt-3 border-t border-purple-200 dark:border-purple-700">
                              <div className="flex items-center gap-4">
                                <span>
                                  <span className="font-bold">{promptsLog.filter(p => p.ai_used).length}</span> AI-powered steps
                                </span>
                                <span>
                                  <span className="font-bold">{promptsLog.filter(p => !p.ai_used).length}</span> Algorithmic steps
                                </span>
                              </div>
                              <span className="text-xs italic">View-only for transparency</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Add to Cart button */}
                    {previewConfirmed && (
                      <button
                        onClick={handleAddToCart}
                        className="w-full py-5 text-lg bg-gradient-to-r from-green-600 to-blue-600 text-white font-bold rounded-lg hover:shadow-2xl transition-all"
                      >
                        🛒 Add to Cart
                      </button>
                    )}
                  </div>
                )}
                
                {jobStatus === 'FAILED' && (
                  <div className="text-center py-20 text-red-500">
                    <div className="text-8xl mb-6">❌</div>
                    <p className="text-lg font-bold">Generation Failed</p>
                    <p className="text-base mt-3">{error || 'Please try again'}</p>
                  </div>
                )}
              </div>
              
              {/* Generate Preview / Regenerate / Design For Me Button - Between Preview and Your Order */}
              {/* Hidden when STL is ready/complete */}
              {!(stlStatus === 'ready' || stlStatus === 'complete') && (
                regenerationCount < 2 ? (
                  <button
                    onClick={regenerationCount === 0 && jobStatus === 'IDLE' ? handleGeneratePreview : handleRegenerate}
                    disabled={imageFiles.length === 0 || (jobStatus !== 'IDLE' && jobStatus !== 'READY')}
                    className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-lg font-semibold rounded-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {jobStatus === 'IDLE' && regenerationCount === 0 && '✨ Generate Preview'}
                    {jobStatus === 'IDLE' && regenerationCount > 0 && `🔄 Regenerate Image (${2 - regenerationCount} remaining)`}
                    {jobStatus === 'READY' && `🔄 Regenerate Image (${2 - regenerationCount} remaining)`}
                    {jobStatus === 'UPLOADING' && '⏳ Uploading Images...'}
                    {jobStatus === 'QUEUED' && '⏳ In Queue...'}
                    {jobStatus === 'PROCESSING' && '⏳ Generating... (1-2 min)'}
                    {jobStatus === 'FAILED' && '❌ Failed - Try Again'}
                  </button>
                ) : (
                  <button
                    onClick={() => !designerRequestSubmitted && setShowDesignerRequestModal(true)}
                    disabled={designerRequestSubmitted}
                    className={`w-full py-4 text-lg font-semibold rounded-lg transition-all shadow-lg ${
                      designerRequestSubmitted
                        ? 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-xl'
                    }`}
                >
                  {designerRequestSubmitted ? '✓ Request Submitted' : '🎨 Design For Me'}
                </button>
                )
              )}
              
              {/* Order Summary - MOVED BELOW PREVIEW - 50% LARGER */}
              <div className={`${theme.card} rounded-lg shadow-2xl p-8 border-2 border-purple-200 dark:border-purple-800`}>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <span className="text-2xl">📋</span>
                  <span>Your Order</span>
                </h3>
                
                {/* Size Selection Summary */}
                <div className="space-y-5 mb-6">
                  <div className="flex justify-between items-center text-lg">
                    <div className="flex-1">
                      <div className="font-bold">Lithophane Ornament</div>
                      <div className="text-base text-gray-500">{selectedSize}</div>
                    </div>
                    
                    {/* Quantity Controls for Ornament */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 py-1">
                        <button
                          onClick={() => setOrnamentQuantity(Math.max(1, ornamentQuantity - 1))}
                          className="w-7 h-7 flex items-center justify-center rounded bg-white dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={ornamentQuantity <= 1}
                          title="Decrease quantity"
                        >
                          <span className="text-lg font-bold text-gray-700 dark:text-gray-300">−</span>
                        </button>
                        <span className="w-8 text-center font-bold text-gray-900 dark:text-gray-100">{ornamentQuantity}</span>
                        <button
                          onClick={() => setOrnamentQuantity(ornamentQuantity + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded bg-white dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                          title="Increase quantity"
                        >
                          <span className="text-lg font-bold text-gray-700 dark:text-gray-300">+</span>
                        </button>
                      </div>
                      <div className="font-bold text-xl text-purple-600 dark:text-purple-400">
                        ${(getBasePrice() * ornamentQuantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Add-ons Summary */}
                {Object.keys(selectedOptions).some(key => selectedOptions[key]) && (
                  <div className="pt-5 border-t border-gray-200 dark:border-gray-700 mb-6">
                    <div className="text-base font-bold text-gray-600 dark:text-gray-400 mb-4">Add-ons:</div>
                    <div className="space-y-4">
                      {addons.filter(addon => selectedOptions[addon.id]).map(addon => (
                        <div key={addon.id} className="flex justify-between items-center text-lg">
                          <div className="text-base truncate flex-1 pr-3">{addon.name}</div>
                          
                          {/* Quantity Controls for Add-on */}
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 py-1">
                              <button
                                onClick={() => setAddonQuantities({
                                  ...addonQuantities,
                                  [addon.id]: Math.max(1, (addonQuantities[addon.id] || 1) - 1)
                                })}
                                className="w-7 h-7 flex items-center justify-center rounded bg-white dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={(addonQuantities[addon.id] || 1) <= 1}
                                title="Decrease quantity"
                              >
                                <span className="text-lg font-bold text-gray-700 dark:text-gray-300">−</span>
                              </button>
                              <span className="w-8 text-center font-bold text-gray-900 dark:text-gray-100">
                                {addonQuantities[addon.id] || 1}
                              </span>
                              <button
                                onClick={() => setAddonQuantities({
                                  ...addonQuantities,
                                  [addon.id]: (addonQuantities[addon.id] || 1) + 1
                                })}
                                className="w-7 h-7 flex items-center justify-center rounded bg-white dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                                title="Increase quantity"
                              >
                                <span className="text-lg font-bold text-gray-700 dark:text-gray-300">+</span>
                              </button>
                            </div>
                            <div className="text-base font-bold text-purple-600 dark:text-purple-400 w-20 text-right">
                              +${(addon.price * (addonQuantities[addon.id] || 1)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Images Count */}
                {imageFiles.length > 0 && (
                  <div className="flex items-center gap-3 text-base text-gray-600 dark:text-gray-400 mb-5">
                    <span className="text-lg">📸</span>
                    <span>{imageFiles.length} image{imageFiles.length > 1 ? 's' : ''} uploaded</span>
                  </div>
                )}
                
                {/* Text Added */}
                {engravingText && (
                  <div className="flex items-center gap-3 text-base text-gray-600 dark:text-gray-400 mb-5">
                    <span className="text-lg">✍️</span>
                    <span>Custom text added</span>
                  </div>
                )}
                
                {/* Total */}
                <div className="pt-5 border-t-2 border-purple-200 dark:border-purple-700">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-xl text-gray-700 dark:text-gray-300">Subtotal:</span>
                    <span className="text-4xl font-bold text-purple-600 dark:text-purple-400">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 text-right mt-2">
                    Tax and shipping calculated at checkout
                  </div>
                </div>
                
                {/* Add to Cart Button - Shown when STL is complete */}
                {(stlStatus === 'ready' || stlStatus === 'complete') && (
                  <div className="mt-6">
                    <button
                      onClick={handleProceedToCheckout}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg rounded-lg transition-colors shadow-lg"
                    >
                      🛒 Add to Cart
                    </button>
                    <p className="text-sm text-gray-500 text-center mt-2">
                      Your generated STL file will be included
                    </p>
                  </div>
                )}
                
                {/* Status Indicator */}
                {jobStatus !== 'IDLE' && jobStatus !== 'READY' && (
                  <div className="mt-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                    <div className="flex items-center gap-3 text-lg">
                      <div className="animate-spin text-xl">⏳</div>
                      <span className="text-blue-700 dark:text-blue-300">
                        {jobStatus === 'UPLOADING' && 'Uploading images...'}
                        {jobStatus === 'QUEUED' && 'In queue...'}
                        {jobStatus === 'PROCESSING' && 'Generating preview...'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Key Features */}
        <div className={`${theme.card} rounded-lg shadow-lg p-6 mt-8`}>
          <h3 className="text-xl font-semibold mb-4">🌟 Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h4 className="font-semibold mb-2">✨ AI-Powered</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Advanced AI technology converts your photos into stunning 3D lithophane designs
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">🎨 Personalized</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Upload your photos and add custom text for a truly unique sphere
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">💡 Illuminated Beauty</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your images glow beautifully when backlit, creating a magical effect
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Lightbox/Zoom Modal */}
      {showLightbox && previewUrl && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95 p-4"
          onClick={handleCloseLightbox}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={handleCloseLightbox}
              className="absolute top-4 right-4 z-10 w-12 h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-lg group"
              title="Close (ESC)"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Zoom Controls */}
            <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
                disabled={zoomLevel >= 5}
                className="w-12 h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom In (+)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
                disabled={zoomLevel <= 0.5}
                className="w-12 h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom Out (-)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleResetZoom(); }}
                className="w-12 h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-lg"
                title="Reset Zoom (0)"
              >
                <span className="text-sm font-bold">1:1</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleFitToScreen(); }}
                className="w-12 h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-lg"
                title="Fit to Screen"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>
            </div>
            
            {/* Instructions */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 px-4 py-2 bg-black bg-opacity-60 rounded-lg text-white text-xs">
              <div className="flex items-center space-x-4">
                <span>🖱️ Scroll to zoom</span>
                <span>✋ Drag to pan</span>
                <span>⌨️ +/- to zoom | 0 to reset | ESC to close</span>
              </div>
            </div>
            
            {/* Zoom Level Indicator */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 px-6 py-3 bg-white dark:bg-gray-800 rounded-full shadow-lg">
              <span className="text-sm font-semibold">{Math.round(zoomLevel * 100)}%</span>
            </div>
            
            {/* Image Container */}
            <div 
              ref={imageContainerRef}
              className="overflow-hidden max-w-full max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
              onWheel={handleWheel}
              style={{ cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`}
                alt="Lithophane Preview - Zoomed"
                className="transition-transform duration-200 select-none"
                style={{ 
                  transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                  transformOrigin: 'center center',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  objectFit: 'contain'
                }}
                draggable={false}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              />
            </div>
          </div>
        </div>
      )}

      {/* PHASE 3: Regeneration Limit Reached Modal */}
      {showRegenerationLimitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 animate-fade-in">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🎨</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Not Happy with Our AI Artist?
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                You've reached the maximum number of regenerations (2). Let our human designers create something perfect for you!
              </p>
            </div>

            {/* Stats */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">Regenerations Used:</span>
                <span className="font-bold text-purple-600 dark:text-purple-400">2 / 2</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowRegenerationLimitModal(false);
                  if (!designerRequestSubmitted) {
                    setShowDesignerRequestModal(true);
                  }
                }}
                disabled={designerRequestSubmitted}
                className={`w-full py-3 font-semibold rounded-lg transition-all ${
                  designerRequestSubmitted
                    ? 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg'
                }`}
              >
                {designerRequestSubmitted ? '✓ Request Submitted' : '🎨 Design For Me'}
              </button>
              <button
                onClick={() => setShowRegenerationLimitModal(false)}
                className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
              >
                Continue with Current Design
              </button>
            </div>

            {/* Info Text */}
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
              Our human designers will work with you to create the perfect ornament
            </p>
          </div>
        </div>
      )}

      {/* PHASE 5: Designer Request Form Modal */}
      {showDesignerRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto pt-20">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 my-8 animate-fade-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">🎨</span>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      Design For Me
                    </h2>
                    <p className="text-green-100 text-sm mt-1">
                      Our human designers will create the perfect ornament for you
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDesignerRequestModal(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all"
                  title="Close"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
              {/* Readonly Fields Section */}
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <span>📋</span> Contact Information (Auto-filled)
                </h3>
                
                {/* Customer Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={user?.name || user?.email?.split('@')[0] || 'Guest User'}
                    readOnly
                    className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user?.email || 'No email provided'}
                    readOnly
                    className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value="Please design my ornament for me"
                    readOnly
                    className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Editable Fields Section */}
              <div className="space-y-4">
                <h3 className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <span>✏️</span> Your Preferences
                </h3>

                {/* Size Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ornament Size <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={designerRequestSize}
                    onChange={(e) => setDesignerRequestSize(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="50mm">50mm - 1 image</option>
                    <option value="75mm">75mm - Up to 2 images</option>
                    <option value="100mm">100mm - Up to 3 images</option>
                  </select>
                </div>

                {/* Image Upload Slots */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upload Your Images (1-3) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => setDesignerRequestImages(Array.from(e.target.files))}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {designerRequestImages.length} image(s) selected. Max 5MB per image.
                  </p>
                </div>

                {/* Background Theme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Background Theme <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={designerRequestTheme}
                    onChange={(e) => setDesignerRequestTheme(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <optgroup label="Seasons">
                      <option value="spring">🌸 Spring</option>
                      <option value="summer">☀️ Summer</option>
                      <option value="fall">🍂 Fall</option>
                      <option value="winter">❄️ Winter</option>
                    </optgroup>
                    <optgroup label="Holidays">
                      <option value="christmas">🎄 Christmas</option>
                      <option value="halloween">🎃 Halloween</option>
                      <option value="easter">🐰 Easter</option>
                      <option value="valentines">❤️ Valentine's Day</option>
                      <option value="thanksgiving">🦃 Thanksgiving</option>
                      <option value="newyear">🎆 New Year</option>
                    </optgroup>
                    <optgroup label="Special Occasions">
                      <option value="birthday">🎂 Birthday</option>
                      <option value="anniversary">💝 Anniversary</option>
                      <option value="wedding">💒 Wedding</option>
                      <option value="graduation">🎓 Graduation</option>
                      <option value="babyshower">👶 Baby Shower</option>
                    </optgroup>
                    <optgroup label="Memorial">
                      <option value="remembrance">🕯️ Remembrance</option>
                      <option value="inmemory">🌹 In Memory</option>
                      <option value="tribute">💐 Tribute</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="nature">🌿 Nature</option>
                    </optgroup>
                  </select>
                </div>

                {/* Optional Text/Engraving */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Optional Text/Engraving
                  </label>
                  <input
                    type="text"
                    value={designerRequestText}
                    onChange={(e) => setDesignerRequestText(e.target.value)}
                    maxLength={20}
                    placeholder="E.g., 'Merry Christmas 2025' (max 20 characters)"
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {designerRequestText.length}/20 characters
                  </p>
                </div>

                {/* PHASE 6: Draft.js Editor for Design Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Design Details & Special Instructions <span className="text-red-500">*</span>
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-700">
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={handleEditorChange}
                      toolbar={{
                        options: ['inline', 'fontSize', 'fontFamily', 'colorPicker', 'textAlign', 'list'],
                        inline: {
                          options: ['bold', 'italic', 'underline']
                        },
                        fontSize: {
                          options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30]
                        },
                        fontFamily: {
                          options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana']
                        },
                        textAlign: {
                          options: ['left', 'center', 'right', 'justify']
                        },
                        list: {
                          options: ['unordered', 'ordered']
                        }
                      }}
                      placeholder="Describe your vision, special requirements, colors, style preferences, etc..."
                      editorStyle={{
                        minHeight: '150px',
                        padding: '10px',
                        fontSize: '14px'
                      }}
                      wrapperClassName="demo-wrapper"
                      editorClassName="demo-editor"
                    />
                  </div>
                  {(() => {
                    const plainTextLength = getEditorPlainTextLength();
                    const isWarning = plainTextLength >= 230;
                    const isDanger = plainTextLength >= 245;
                    
                    return (
                      <p className={`text-xs mt-2 font-medium ${
                        isDanger ? 'text-red-600 dark:text-red-400' :
                        isWarning ? 'text-orange-600 dark:text-orange-400' :
                        'text-gray-500 dark:text-gray-400'
                      }`}>
                        {plainTextLength}/250 characters
                        {isWarning && !isDanger && ' - Approaching limit'}
                        {isDanger && ' - Character limit almost reached!'}
                      </p>
                    );
                  })()}
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">💡</span>
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-semibold mb-1">How it works:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
                      <li>Our designers will review your preferences</li>
                      <li>They'll create a custom design just for you</li>
                      <li>You'll receive a preview within 24-48 hours</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-b-2xl border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => setShowDesignerRequestModal(false)}
                disabled={isSubmittingDesignerRequest}
                className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDesignerRequest}
                disabled={!isFormValid || isSubmittingDesignerRequest}
                className={`flex-1 py-3 font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                  isFormValid && !isSubmittingDesignerRequest
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
                title={!isFormValid ? 'Please fill in all required fields' : 'Submit designer request'}
              >
                {isSubmittingDesignerRequest ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Adding to cart...</span>
                  </>
                ) : (
                  '🛒 Add to Cart'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE 9: Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 animate-fade-in">
            {/* Success Icon */}
            <div className="text-center mb-6">
              <div className="inline-block p-4 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                <svg className="w-16 h-16 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Design Request Submitted Successfully!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Your request has been received by our design team
              </p>
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-5 mb-6">
              <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                <span>📋</span> What Happens Next?
              </h3>
              <ol className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-purple-600 dark:text-purple-400 mt-0.5">1.</span>
                  <span>Our designers will review your preferences and images</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-purple-600 dark:text-purple-400 mt-0.5">2.</span>
                  <span>They'll create a custom design tailored to your specifications</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-purple-600 dark:text-purple-400 mt-0.5">3.</span>
                  <span><strong>You'll receive a preview within 24-48 hours</strong></span>
                </li>
              </ol>
            </div>

            {/* Confirmation Email Note */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">📧</span>
                <div className="text-sm">
                  <p className="font-semibold text-blue-800 dark:text-blue-300 mb-1">
                    Check your email
                  </p>
                  <p className="text-blue-700 dark:text-blue-400">
                    We've sent a confirmation email with your request details and a unique request ID for tracking.
                  </p>
                </div>
              </div>
            </div>

            {/* OK Button */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              OK, Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LithophaneSpherePage;
